import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import MuiFormHelperText from '@mui/material/FormHelperText';
import MuiInputAdornment from '@mui/material/InputAdornment';
import MuiOutlinedInput from '@mui/material/OutlinedInput';
import { useTheme } from '@mui/material/styles';
import * as React from 'react';
import { FlexBox } from '../box';
import { useId } from '../hooks';
import { getIcon } from '../icon';
import { styled } from '../styled';
import { fieldErrorProps } from '../textfield/utils';
import { measureText } from '../typography';
import { withVersion } from '../utils';
import { getFilterTextFieldStyles, getResolvedState, useFontLoadRemeasure } from './utils';
const HelperText = styled(MuiFormHelperText)(({ theme }) => ({
    padding: theme.spacing(0.25, 0.75, 0, 0.75)
}));
const StyledInput = styled(MuiOutlinedInput)(({ theme, $hasValue, $inputWidth, $state }) => {
    const styles = getFilterTextFieldStyles(theme, $state);
    return {
        height: 30,
        width: 'min-content',
        padding: '4px 6px',
        borderRadius: theme.shape.borderRadius,
        transition: 'none',
        backgroundColor: styles.backgroundColor,
        boxShadow: styles.boxShadow,
        '& .MuiOutlinedInput-input': {
            width: $inputWidth,
            padding: 0,
            minWidth: 0,
            height: 22,
            boxSizing: 'border-box',
            color: styles.color,
            fontWeight: theme.palette.components?.['filter-textfield']?.fontWeight,
            fontSize: theme.typography.body2.fontSize,
            lineHeight: theme.typography.body2.lineHeight,
            letterSpacing: theme.typography.body2.letterSpacing,
            '&::placeholder': {
                color: styles.placeholderColor,
                opacity: $hasValue ? 0 : 1
            }
        },
        '& .MuiOutlinedInput-notchedOutline, &:hover .MuiOutlinedInput-notchedOutline, &.Mui-focused .MuiOutlinedInput-notchedOutline, &.Mui-error .MuiOutlinedInput-notchedOutline': {
            border: 0,
            borderWidth: 0,
            borderColor: 'transparent',
            transition: 'none'
        },
        '&:hover': {
            backgroundColor: styles.hover.backgroundColor,
            boxShadow: styles.hover.boxShadow
        },
        '&.Mui-focused': {
            backgroundColor: styles.focus.backgroundColor,
            boxShadow: styles.focus.boxShadow,
            '& .MuiOutlinedInput-input::placeholder': {
                color: styles.focus.placeholderColor
            }
        },
        '&.Mui-disabled': {
            backgroundColor: styles.backgroundColor,
            boxShadow: 'none'
        }
    };
});
const StartAdornment = styled(MuiInputAdornment)(() => ({
    marginRight: 4,
    marginLeft: 0,
    width: 20,
    height: 20,
    maxHeight: 20
}));
const FilterTextFieldImpl = React.forwardRef((props, ref) => {
    const { className, value, placeholder, disabled = false, error: errorProp, helperText: helperTextProp, icon, iconProps, onChange, slotProps, 'data-role': dataRole = 'filter-textfield', 'data-role-value': dataRoleValue, ...muiProps } = props;
    const { error, helperText } = fieldErrorProps(errorProp, helperTextProp);
    const hasValue = value !== '';
    const [focused, setFocused] = React.useState(false);
    const fontLoadRemeasureVersion = useFontLoadRemeasure();
    const theme = useTheme();
    const helperId = useId();
    const resolvedState = getResolvedState({
        error,
        disabled,
        focused,
        hasValue
    });
    const iconColor = (() => {
        switch (resolvedState) {
            case 'disabled':
                return 'text.disabled';
            case 'error':
                return 'components.filter-textfield.error.icon';
            case 'focused':
                return 'components.filter-textfield.focused.icon';
            case 'filled':
                return 'components.filter-textfield.filled.icon';
            default:
                return 'components.filter-textfield.enabled.icon';
        }
    })();
    const Icon = icon ? getIcon(icon) : undefined;
    void fontLoadRemeasureVersion;
    const { input: slotInputProps, ...otherSlotProps } = slotProps ?? {};
    const describedBy = (helperText
        ? [slotInputProps?.['aria-describedby'], helperId]
            .filter(Boolean)
            .join(' ')
        : slotInputProps?.['aria-describedby']) || undefined;
    const mergedInputSlotProps = {
        ...slotInputProps,
        'data-1p-ignore': true,
        'data-role': dataRole,
        ...(describedBy ? { 'aria-describedby': describedBy } : {}),
        ...(dataRoleValue
            ? {
                'data-role-value': dataRoleValue
            }
            : {}),
        sx: slotInputProps?.sx
    };
    return (_jsxs(FlexBox, { flexDirection: "column", alignItems: "flex-start", children: [_jsx(StyledInput, { ...muiProps, ref: ref, className: className, value: value, disabled: disabled, error: error, placeholder: placeholder, "$hasValue": hasValue, "$inputWidth": measureText(theme, value || placeholder || '', 'bodyDefault'), "$state": resolvedState, onFocus: (event) => {
                    setFocused(true);
                    muiProps.onFocus?.(event);
                }, onBlur: (event) => {
                    setFocused(false);
                    muiProps.onBlur?.(event);
                }, startAdornment: Icon ? (_jsx(StartAdornment, { position: "start", disablePointerEvents: true, children: _jsx(Icon, { ...iconProps, filled: focused || hasValue, sx: {
                            color: iconColor,
                            fontSize: 20
                        } }) })) : undefined, slotProps: {
                    ...otherSlotProps,
                    input: mergedInputSlotProps
                }, onChange: (event) => onChange(event.target.value, event) }), helperText && (_jsx(HelperText, { id: helperId, error: error, children: helperText }))] }));
});
FilterTextFieldImpl.displayName =
    'FilterTextField';
export const FilterTextField = withVersion(FilterTextFieldImpl, '1.0.0');
